// src/components/ClientPortalTimeExpenses.js

import React from 'react';
import { formatCurrency } from '../utils/currency';

function ClientPortalTimeExpenses({ currentUser, clients, timeEntries, expenseEntries, cases }) {
  // Find the specific client profile linked to the current user
  const clientProfile = clients.find(client => client.user === currentUser.id);

  // Filter time entries relevant to this specific client profile or their cases
  const clientTimeEntries = timeEntries.filter(entry =>
    entry.client === (clientProfile ? clientProfile.id : null) ||
    (entry.case && cases.some(c => c.id === entry.case && c.client === (clientProfile ? clientProfile.id : null)))
  );

  // Filter expense entries relevant to this specific client profile or their cases
  const clientExpenseEntries = expenseEntries.filter(entry =>
    entry.client === (clientProfile ? clientProfile.id : null) ||
    (entry.case && cases.some(c => c.id === entry.case && c.client === (clientProfile ? clientProfile.id : null)))
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-primary mb-1">Your Time & Expenses</h2>
      {clientProfile ? (
        <p className="text-gray-500 mb-6">Review time logged and expenses incurred for your cases, {clientProfile.first_name} {clientProfile.last_name}.</p>
      ) : (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <p className="text-yellow-700">Your user account is not yet linked to a client profile. Please contact the firm administrator to view your time and expenses.</p>
        </div>
      )}

      <h3 className="text-lg font-semibold text-gray-700 mb-3">Time Entries</h3>
      {clientTimeEntries.length > 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden mb-8">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Hours</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Associated Case</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Logged By</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(clientTimeEntries || []).map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.hours}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">{entry.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.case_title || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.logged_by_username || 'N/A'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center mb-8">
          <p className="text-blue-700">No time entries found associated with your client profile or cases.</p>
        </div>
      )}

      <h3 className="text-lg font-semibold text-gray-700 mb-3">Disbursements</h3>
      {clientExpenseEntries.length > 0 ? (
        <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Description</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Associated Case</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Incurred By</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Receipt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(clientExpenseEntries || []).map(entry => (
                  <tr key={entry.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.date}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 font-medium">{formatCurrency(entry.amount)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.category}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.case_title || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{entry.incurred_by_username || 'N/A'}</td>
                    <td className="px-4 py-3 text-sm">
                      {entry.receipt ? (
                        <a href={entry.receipt} target="_blank" rel="noopener noreferrer" className="px-3 py-1 border border-accent text-accent text-xs font-medium rounded-full hover:bg-accent hover:text-white transition-colors">View Receipt</a>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-700">No disbursements found associated with your client profile or cases.</p>
        </div>
      )}
    </div>
  );
}

export default ClientPortalTimeExpenses;
